
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, X, MapPin, Clock, DollarSign, Building, ExternalLink, Calendar } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Job {
  id: string;
  job_title: string;
  company_name: string;
  job_description: string;
  requirements: string | null;
  salary_range: string | null;
  job_type: string;
  location_type: string;
  state: string;
  local_government: string;
  contact_email: string;
  contact_phone: string | null;
  website_url: string | null;
  posted_by: string | null;
  approved: boolean;
  created_at: string;
  expires_at: string | null;
}

interface JobsTableProps {
  jobs: Job[];
  isAdmin: boolean;
  onToggleApproval: (id: string, currentStatus: boolean) => void;
  onApplyToJob: (job: Job) => void;
}

const JobsTable = ({ jobs, isAdmin, onToggleApproval, onApplyToJob }: JobsTableProps) => {
  const getJobTypeColor = (type: string) => {
    switch (type) {
      case 'full-time': return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'part-time': return 'bg-green-50 text-green-800 border-green-200';
      case 'contract': return 'bg-purple-50 text-purple-800 border-purple-200';
      case 'freelance': return 'bg-orange-50 text-orange-800 border-orange-200';
      default: return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  const getLocationTypeIcon = (type: string) => {
    switch (type) {
      case 'remote': return '🏠';
      case 'hybrid': return '🔄';
      default: return '🏢';
    }
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const visibleJobs = jobs.filter(job => job.approved || isAdmin);

  if (visibleJobs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-600">
        No job opportunities available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {visibleJobs.map((job) => (
        <Card key={job.id} className={`p-6 bg-white/95 backdrop-blur-sm border transition-all hover:shadow-md ${isExpired(job.expires_at) ? 'opacity-60' : ''}`}>
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl font-semibold text-gray-900">{job.job_title}</h3>
                {isExpired(job.expires_at) && (
                  <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200">
                    Expired
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                <div className="flex items-center gap-1">
                  <Building size={16} />
                  {job.company_name}
                </div>
                <div className="flex items-center gap-1">
                  <MapPin size={16} />
                  {job.local_government}, {job.state}
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={16} />
                  {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className={getJobTypeColor(job.job_type)}>
                  {job.job_type.charAt(0).toUpperCase() + job.job_type.slice(1)}
                </Badge>
                <Badge variant="outline" className="bg-gray-50 text-gray-800 border-gray-200">
                  {getLocationTypeIcon(job.location_type)} {job.location_type.charAt(0).toUpperCase() + job.location_type.slice(1)}
                </Badge>
                {job.salary_range && (
                  <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200">
                    <DollarSign size={12} className="mr-1" />
                    {job.salary_range}
                  </Badge>
                )}
              </div>

              <p className="text-gray-700 mb-3 line-clamp-2">{job.job_description}</p>

              {job.expires_at && (
                <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                  <Calendar size={14} />
                  Application deadline: {new Date(job.expires_at).toLocaleDateString()}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => onApplyToJob(job)}
                  disabled={isExpired(job.expires_at)}
                  className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 text-white"
                  size="sm"
                >
                  {isExpired(job.expires_at) ? 'Application Closed' : 'Apply Now'}
                </Button>
                
                {job.website_url && (
                  <Button variant="outline" size="sm" asChild>
                    <a href={job.website_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink size={14} className="mr-1" />
                      Company Website
                    </a>
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              {job.approved ? (
                <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200 font-medium">
                  Active
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200 font-medium">
                  Pending Review
                </Badge>
              )}
              
              {isAdmin && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onToggleApproval(job.id, job.approved)}
                >
                  {job.approved ? (
                    <X size={16} className="text-red-500" />
                  ) : (
                    <Check size={16} className="text-green-500" />
                  )}
                </Button>
              )}
            </div>
          </div>

          {job.requirements && (
            <div className="border-t pt-3">
              <h4 className="font-medium text-gray-900 mb-2">Requirements:</h4>
              <p className="text-sm text-gray-600 line-clamp-3">{job.requirements}</p>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
};

export default JobsTable;
